import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import XPControlPanel from '../components/admin/XPControlPanel';
import RewardClaimsPanel from '../components/admin/RewardClaimsPanel';
import {
  ArrowLeft,
  Zap,
  Sparkles,
  Users,
  Hash,
  Plus,
  Trash2,
  Edit,
  Shield,
  Crown,
  Star,
  User,
  UserPlus,
  Settings,
  Lock,
  Unlock,
  Save,
  FolderPlus,
  AlertTriangle,
  Video,
  Megaphone,
  HelpCircle,
  FolderOpen,
  BarChart3,
  Key,
  BookOpen,
  MessageSquare,
  Check,
  X,
  Trophy,
  Target,
  Gift,
  Percent,
  Ship,
  Compass,
  DollarSign
} from 'lucide-react';

const AdminPage = () => {
  const { user, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelGroups, setChannelGroups] = useState([]);
  const [reports, setReports] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Gamification states
  const [challenges, setChallenges] = useState([]);
  const [levelRewards, setLevelRewards] = useState([]);
  const [ambassadorTiers, setAmbassadorTiers] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [pendingReferrals, setPendingReferrals] = useState([]);
  
  // Parcours states (chapters & milestones)
  const [chapters, setChapters] = useState([]);
  const [milestones, setMilestones] = useState([]);
  
  // Dialogs
  const [createMemberOpen, setCreateMemberOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [createFaqOpen, setCreateFaqOpen] = useState(false);
  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editChannelOpen, setEditChannelOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  
  // Form states
  const [newMember, setNewMember] = useState({ email: '', first_name: '', last_name: '', role: 'odys_start', password: '' });
  const [newChannel, setNewChannel] = useState({ name: '', description: '', is_private: false, allowed_roles: [], channel_type: 'text', group_id: '', tags: '', is_read_only: false });
  const [newGroup, setNewGroup] = useState({ name: '', order: 0 });

  // Memoize derived data — avoid expensive re-filters on every render
  const channelsByGroupId = useMemo(() => {
    const map = new Map();
    for (const c of channels) {
      const gid = c.group_id;
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push(c);
    }
    return map;
  }, [channels]);

  const pendingReferralsList = useMemo(
    () => pendingReferrals.filter(r => r.status === 'pending'),
    [pendingReferrals]
  );
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: 'general' });
  
  // Gamification form states
  const [createChallengeOpen, setCreateChallengeOpen] = useState(false);
  const [createRewardOpen, setCreateRewardOpen] = useState(false);
  const [newChallenge, setNewChallenge] = useState({ title: '', description: '', challenge_type: 'messages', target_count: 5, xp_reward: 50, period: 'weekly', is_active: true });
  const [newReward, setNewReward] = useState({ level: 1, title: '', description: '', reward_type: 'custom', value: 0, icon: '🎁', requires_address: false, requires_recipient: false, requires_booking: false });
  const [editRewardOpen, setEditRewardOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [editTiersOpen, setEditTiersOpen] = useState(false);
  const [editedTiers, setEditedTiers] = useState([]);
  
  // Parcours form states
  const [createChapterOpen, setCreateChapterOpen] = useState(false);
  const [createMilestoneOpen, setCreateMilestoneOpen] = useState(false);
  const [editChapterOpen, setEditChapterOpen] = useState(false);
  const [editMilestoneOpen, setEditMilestoneOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [generatingKeyPoints, setGeneratingKeyPoints] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [newChapter, setNewChapter] = useState({ title: '', description: '', icon: '📚', xp_reward: 50 });
  const [newMilestone, setNewMilestone] = useState({ id: '', title: '', description: '', icon: '🎯', xp_reward: 100 });

  useEffect(() => {
    if (!isAdmin && user.role !== 'major') {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const generateKeyPointsFor = async (chapterId) => {
    setGeneratingKeyPoints(true);
    try {
      const res = await api.post(`/admin/chapters/${chapterId}/generate-key-points`);
      setEditingChapter(prev => prev ? {
        ...prev,
        key_points: res.data.key_points,
        key_points_source: 'ai_draft',
        key_points_validated: false,
      } : prev);
      toast.success('Brouillon généré — relis-le avant d\'enregistrer');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de génération');
    } finally {
      setGeneratingKeyPoints(false);
    }
  };

  const generateMissingKeyPoints = async () => {
    setGeneratingKeyPoints(true);
    try {
      const res = await api.post('/admin/chapters/generate-key-points-missing');
      toast.success(res.data.message);
      setTimeout(fetchData, 45000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de génération');
    } finally {
      setGeneratingKeyPoints(false);
    }
  };

  const validateKeyPoints = async (chapterId) => {
    try {
      const res = await api.post(`/admin/chapters/${chapterId}/validate-key-points`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const fetchData = async () => {
    try {
      const [membersRes, channelsRes, groupsRes, reportsRes, faqRes, challengesRes, rewardsRes, tiersRes, referralsRes, payoutsRes, chaptersRes, milestonesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/channels'),
        api.get('/channel-groups'),
        api.get('/reports'),
        api.get('/faq'),
        api.get('/challenges/all').catch(() => ({ data: [] })),
        api.get('/level-rewards').catch(() => ({ data: [] })),
        api.get('/ambassador/tiers').catch(() => ({ data: [] })),
        api.get('/ambassador/referrals').catch(() => ({ data: [] })),
        api.get('/admin/ambassador/payouts').catch(() => ({ data: [] })),
        api.get('/chapters').catch(() => ({ data: [] })),
        api.get('/milestones/definitions').catch(() => ({ data: [] }))
      ]);
      setMembers(membersRes.data);
      setChannels(channelsRes.data);
      setChannelGroups(groupsRes.data);
      setReports(reportsRes.data);
      setFaq(faqRes.data);
      setChallenges(challengesRes.data || []);
      setLevelRewards(rewardsRes.data || []);
      setAmbassadorTiers(tiersRes.data || []);
      setPendingReferrals(referralsRes.data || []);
      setPendingPayouts(payoutsRes.data || []);
      setChapters(chaptersRes.data || []);
      setMilestones(milestonesRes.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.email || !newMember.first_name || !newMember.last_name) {
      toast.error('Champs obligatoires manquants');
      return;
    }
    try {
      await api.post('/admin/users', newMember);
      toast.success('Membre créé !');
      setCreateMemberOpen(false);
      setNewMember({ email: '', first_name: '', last_name: '', role: 'odys_start', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    try {
      await api.put(`/admin/users/${editingMember.id}`, { role: editingMember.role, is_active: editingMember.is_active });
      toast.success('Membre mis à jour');
      setEditMemberOpen(false);
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Supprimer ce membre ?')) return;
    try {
      await api.delete(`/admin/users/${memberId}`);
      toast.success('Membre supprime');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleResetPassword = async (memberId, memberName) => {
    if (!confirm(`Reinitialiser le mot de passe de ${memberName} ?`)) return;
    try {
      const response = await api.post(`/admin/users/${memberId}/reset-password`);
      const { temp_password, email } = response.data;
      toast.success(
        <div>
          <p className="font-bold">Mot de passe reinitialise</p>
          <p className="text-sm mt-1">Email: {email}</p>
          <p className="text-sm font-mono bg-black/20 p-1 rounded mt-1">{temp_password}</p>
          <p className="text-xs mt-1 opacity-70">Copiez et envoyez ce mot de passe au membre</p>
        </div>,
        { duration: 15000 }
      );
    } catch (error) {
      toast.error('Erreur lors de la reinitialisation');
    }
  };

  const handleCreateChannel = async (e) => {
    e.preventDefault();
    if (!newChannel.name) {
      toast.error('Nom requis');
      return;
    }
    try {
      const channelData = {
        ...newChannel,
        tags: newChannel.tags ? newChannel.tags.split(',').map(t => t.trim()) : [],
        group_id: newChannel.group_id || null
      };
      await api.post('/channels', channelData);
      toast.success('Channel créé !');
      setCreateChannelOpen(false);
      setNewChannel({ name: '', description: '', is_private: false, allowed_roles: [], channel_type: 'text', group_id: '', tags: '', is_read_only: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!confirm('Supprimer ce channel et tous ses messages ?')) return;
    try {
      await api.delete(`/channels/${channelId}`);
      toast.success('Channel supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateChannel = async () => {
    if (!editingChannel) return;
    try {
      const channelData = {
        name: editingChannel.name,
        description: editingChannel.description || '',
        is_private: editingChannel.is_private || false,
        allowed_roles: editingChannel.allowed_roles || [],
        channel_type: editingChannel.channel_type || 'text',
        group_id: editingChannel.group_id === 'none' ? null : editingChannel.group_id,
        tags: editingChannel.tags || [],
        is_read_only: editingChannel.is_read_only || false
      };
      await api.put(`/channels/${editingChannel.id}`, channelData);
      toast.success('Channel mis à jour !');
      setEditChannelOpen(false);
      setEditingChannel(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name) {
      toast.error('Nom requis');
      return;
    }
    try {
      await api.post('/channel-groups', newGroup);
      toast.success('Groupe créé !');
      setCreateGroupOpen(false);
      setNewGroup({ name: '', order: 0 });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Supprimer ce groupe ?')) return;
    try {
      await api.delete(`/channel-groups/${groupId}`);
      toast.success('Groupe supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCreateFaq = async (e) => {
    e.preventDefault();
    if (!newFaq.question || !newFaq.answer) {
      toast.error('Champs requis');
      return;
    }
    try {
      await api.post('/faq', newFaq);
      toast.success('FAQ créée !');
      setCreateFaqOpen(false);
      setNewFaq({ question: '', answer: '', category: 'general' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    try {
      await api.delete(`/faq/${faqId}`);
      toast.success('FAQ supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReportAction = async (reportId, status) => {
    try {
      await api.put(`/reports/${reportId}?status=${status}`);
      toast.success('Signalement traité');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 text-red-400" />;
      case 'announcements': return <Megaphone className="w-4 h-4 text-yellow-400" />;
      case 'qa': return <HelpCircle className="w-4 h-4 text-blue-400" />;
      case 'resources': return <FolderOpen className="w-4 h-4 text-green-400" />;
      default: return <Hash className="w-4 h-4" />;
    }
  };

  const getRoleLabel = (role) => {
    const labels = { admin: 'Admin', major: 'Major', ambassadeur: 'Ambassadeur', odys_mentoring: 'Mentoring', odys_start: 'Start' };
    return labels[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classes = { admin: 'role-admin', major: 'role-major', ambassadeur: 'role-ambassadeur', odys_mentoring: 'role-odys_mentoring', odys_start: 'role-odys_start' };
    return classes[role] || '';
  };

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'major', label: 'Major (Coach)' },
    { value: 'community_manager', label: 'Community Manager' },
    { value: 'ambassadeur', label: 'Ambassadeur' },
    { value: 'odys_mentoring', label: "Odys'Mentoring" },
    { value: 'odys_start', label: "Odys'Start" }
  ];

  const channelTypes = [
    { value: 'text', label: 'Texte', icon: <Hash className="w-4 h-4" /> },
    { value: 'video', label: 'Vidéo', icon: <Video className="w-4 h-4" /> },
    { value: 'announcements', label: 'Annonces', icon: <Megaphone className="w-4 h-4" /> },
    { value: 'qa', label: 'Q&R', icon: <HelpCircle className="w-4 h-4" /> },
    { value: 'resources', label: 'Ressources', icon: <FolderOpen className="w-4 h-4" /> }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Settings className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <div className="noise-overlay" />

      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Administration</h1>
                <p className="text-sm text-muted-foreground">Gérer la communauté</p>
              </div>
            </div>
          </div>
          <Button onClick={() => navigate('/analytics')} className="bg-primary text-primary-foreground gold-glow">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="members" className="space-y-6">
          <TabsList className="bg-secondary/50 flex-wrap">
            <TabsTrigger value="members"><Users className="w-4 h-4 mr-2" />Membres ({members.length})</TabsTrigger>
            <TabsTrigger value="channels"><Hash className="w-4 h-4 mr-2" />Channels ({channels.length})</TabsTrigger>
            <TabsTrigger value="groups"><FolderPlus className="w-4 h-4 mr-2" />Groupes ({channelGroups.length})</TabsTrigger>
            <TabsTrigger value="moderation"><AlertTriangle className="w-4 h-4 mr-2" />Modération ({reports.filter(r => r.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="faq"><BookOpen className="w-4 h-4 mr-2" />FAQ ({faq.length})</TabsTrigger>
            <TabsTrigger value="gamification"><Trophy className="w-4 h-4 mr-2" />Gamification</TabsTrigger>
            <TabsTrigger value="ambassador"><Percent className="w-4 h-4 mr-2" />Ambassadeurs ({pendingReferrals.filter(r => r.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="parcours"><Ship className="w-4 h-4 mr-2" />Parcours ({chapters.length})</TabsTrigger>
            <TabsTrigger value="xp" data-testid="admin-xp-tab"><Zap className="w-4 h-4 mr-2" />XP & Anti-abus</TabsTrigger>
            <TabsTrigger value="rewards" data-testid="admin-rewards-tab"><Star className="w-4 h-4 mr-2" />Récompenses</TabsTrigger>
          </TabsList>

          {/* Reward claims Tab */}
          <TabsContent value="rewards" className="space-y-6">
            <RewardClaimsPanel />
          </TabsContent>

          {/* XP & anti-abuse Tab */}
          <TabsContent value="xp" className="space-y-6">
            <XPControlPanel members={members} />
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold">Gestion des membres</h2>
              {isAdmin && (
                <Dialog open={createMemberOpen} onOpenChange={setCreateMemberOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-primary-foreground gold-glow"><UserPlus className="w-4 h-4 mr-2" />Nouveau membre</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Créer un membre</DialogTitle>
                      <DialogDescription>Les identifiants seront envoyés par email</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMember} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Prénom *</Label>
                          <Input value={newMember.first_name} onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })} className="bg-secondary/50" />
                        </div>
                        <div className="space-y-2">
                          <Label>Nom *</Label>
                          <Input value={newMember.last_name} onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })} className="bg-secondary/50" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} className="bg-secondary/50" />
                      </div>
                      <div className="space-y-2">
                        <Label>Rôle</Label>
                        <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                          <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCreateMemberOpen(false)}>Annuler</Button>
                        <Button type="submit" className="bg-primary text-primary-foreground">Créer</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <Card className="card-base">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Niveau</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">{member.first_name} {member.last_name}</TableCell>
                        <TableCell className="text-muted-foreground">{member.email}</TableCell>
                        <TableCell><Badge className={getRoleBadgeClass(member.role)}>{getRoleLabel(member.role)}</Badge></TableCell>
                        <TableCell>{member.level || 1}</TableCell>
                        <TableCell>{member.points || 0}</TableCell>
                        <TableCell><Badge variant={member.is_active ? 'default' : 'secondary'}>{member.is_active ? 'Actif' : 'Inactif'}</Badge></TableCell>
                        <TableCell className="text-right">
                          {isAdmin && member.id !== user.id && (
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" title="Modifier" onClick={() => { setEditingMember(member); setEditMemberOpen(true); }}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" title="Reinitialiser MDP" className="hover:bg-orange-500/10 hover:text-orange-500" onClick={() => handleResetPassword(member.id, `${member.first_name} ${member.last_name}`)} data-testid={`reset-pwd-${member.id}`}><Key className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="hover:bg-destructive/10 hover:text-destructive" title="Supprimer" onClick={() => handleDeleteMember(member.id)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Channels Tab */}
          <TabsContent value="channels" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold">Gestion des channels</h2>
              <Dialog open={createChannelOpen} onOpenChange={setCreateChannelOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground gold-glow"><Plus className="w-4 h-4 mr-2" />Nouveau channel</Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Créer un channel</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateChannel} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input value={newChannel.name} onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })} placeholder="nom-du-channel" className="bg-secondary/50" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={newChannel.description} onChange={(e) => setNewChannel({ ...newChannel, description: e.target.value })} className="bg-secondary/50" rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Type de channel</Label>
                      <Select value={newChannel.channel_type} onValueChange={(v) => setNewChannel({ ...newChannel, channel_type: v })}>
                        <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {channelTypes.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              <div className="flex items-center gap-2">{t.icon}{t.label}</div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Groupe</Label>
                      <Select value={newChannel.group_id || "none"} onValueChange={(v) => setNewChannel({ ...newChannel, group_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-secondary/50"><SelectValue placeholder="Aucun groupe" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun groupe</SelectItem>
                          {channelGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tags (séparés par virgule)</Label>
                      <Input value={newChannel.tags} onChange={(e) => setNewChannel({ ...newChannel, tags: e.target.value })} placeholder="immobilier, formation, tips" className="bg-secondary/50" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        {newChannel.is_private ? <Lock className="w-4 h-4 text-primary" /> : <Unlock className="w-4 h-4" />}
                        <Label>Channel privé</Label>
                      </div>
                      <Switch checked={newChannel.is_private} onCheckedChange={(c) => setNewChannel({ ...newChannel, is_private: c })} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-yellow-500" />
                        <div>
                          <Label>Lecture seule</Label>
                          <p className="text-xs text-muted-foreground">Seuls Admin et Major peuvent poster</p>
                        </div>
                      </div>
                      <Switch checked={newChannel.is_read_only} onCheckedChange={(c) => setNewChannel({ ...newChannel, is_read_only: c })} />
                    </div>
                    {newChannel.is_private && (
                      <div className="space-y-2">
                        <Label>Rôles autorisés</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {roles.map((r) => (
                            <div key={r.value} className="flex items-center space-x-2">
                              <Checkbox id={`role-${r.value}`} checked={newChannel.allowed_roles.includes(r.value)} onCheckedChange={(c) => {
                                if (c) setNewChannel({ ...newChannel, allowed_roles: [...newChannel.allowed_roles, r.value] });
                                else setNewChannel({ ...newChannel, allowed_roles: newChannel.allowed_roles.filter(x => x !== r.value) });
                              }} />
                              <Label htmlFor={`role-${r.value}`} className="text-sm cursor-pointer">{r.label}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateChannelOpen(false)}>Annuler</Button>
                      <Button type="submit" className="bg-primary text-primary-foreground">Créer</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channels.map((channel) => (
                <Card key={channel.id} className="card-base">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {getChannelIcon(channel.channel_type)}
                        <CardTitle className="text-lg">{channel.name}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        {channel.is_private && <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1" />Privé</Badge>}
                      </div>
                    </div>
                    <CardDescription className="line-clamp-2">{channel.description || 'Pas de description'}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {channel.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {channel.tags.map(tag => <span key={tag} className="tag">#{tag}</span>)}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{channelTypes.find(t => t.value === channel.channel_type)?.label || 'Texte'}</Badge>
                        {channel.is_read_only && (
                          <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                            <Lock className="w-3 h-3 mr-1" />
                            Lecture seule
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingChannel({...channel, group_id: channel.group_id || 'none'}); setEditChannelOpen(true); }}>
                          <Edit className="w-4 h-4 mr-1" />Modifier
                        </Button>
                        {isAdmin && channel.created_by !== 'system' && (
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteChannel(channel.id)}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold">Groupes de channels</h2>
              <Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground gold-glow"><FolderPlus className="w-4 h-4 mr-2" />Nouveau groupe</Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Créer un groupe</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateGroup} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value.toUpperCase() })} placeholder="NOM DU GROUPE" className="bg-secondary/50" />
                    </div>
                    <div className="space-y-2">
                      <Label>Ordre d&apos;affichage</Label>
                      <Input type="number" value={newGroup.order} onChange={(e) => setNewGroup({ ...newGroup, order: parseInt(e.target.value) })} className="bg-secondary/50" />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateGroupOpen(false)}>Annuler</Button>
                      <Button type="submit" className="bg-primary text-primary-foreground">Créer</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channelGroups.map((group) => (
                <Card key={group.id} className="card-base">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading">{group.name}</CardTitle>
                      <Badge variant="outline">Ordre: {group.order}</Badge>
                    </div>
                    <CardDescription>{group.channels?.length || 0} channels</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {(channelsByGroupId.get(group.id) || []).map(ch => (
                        <div key={ch.id} className="flex items-center gap-2 text-sm p-2 bg-secondary/20 rounded">
                          {getChannelIcon(ch.channel_type)}
                          <span>{ch.name}</span>
                        </div>
                      ))}
                    </div>
                    {isAdmin && (
                      <Button variant="destructive" size="sm" className="w-full" onClick={() => handleDeleteGroup(group.id)}><Trash2 className="w-4 h-4 mr-2" />Supprimer</Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation" className="space-y-4">
            <h2 className="font-heading text-xl font-semibold">Signalements</h2>
            <Card className="card-base">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Message ID</TableHead>
                      <TableHead>Raison</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun signalement</TableCell>
                      </TableRow>
                    ) : (
                      reports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell className="font-mono text-xs">{report.message_id.slice(0, 8)}...</TableCell>
                          <TableCell>{report.reason}</TableCell>
                          <TableCell>
                            <Badge variant={report.status === 'pending' ? 'default' : report.status === 'reviewed' ? 'outline' : 'secondary'}>
                              {report.status === 'pending' ? 'En attente' : report.status === 'reviewed' ? 'Traité' : 'Rejeté'}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(report.created_at).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell className="text-right">
                            {report.status === 'pending' && (
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="icon" className="text-green-500" onClick={() => handleReportAction(report.id, 'reviewed')}><Check className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleReportAction(report.id, 'dismissed')}><X className="w-4 h-4" /></Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold">FAQ / Bot</h2>
              <Dialog open={createFaqOpen} onOpenChange={setCreateFaqOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground gold-glow"><Plus className="w-4 h-4 mr-2" />Nouvelle FAQ</Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Ajouter une FAQ</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateFaq} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Question *</Label>
                      <Input value={newFaq.question} onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} className="bg-secondary/50" />
                    </div>
                    <div className="space-y-2">
                      <Label>Réponse *</Label>
                      <Textarea value={newFaq.answer} onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })} className="bg-secondary/50" rows={4} />
                    </div>
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <Select value={newFaq.category} onValueChange={(v) => setNewFaq({ ...newFaq, category: v })}>
                        <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">Général</SelectItem>
                          <SelectItem value="débutant">Débutant</SelectItem>
                          <SelectItem value="support">Support</SelectItem>
                          <SelectItem value="contenu">Contenu</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateFaqOpen(false)}>Annuler</Button>
                      <Button type="submit" className="bg-primary text-primary-foreground">Créer</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {faq.map((item) => (
                <Card key={item.id} className="card-base">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{item.question}</CardTitle>
                      <Button variant="ghost" size="icon" className="hover:text-destructive" onClick={() => handleDeleteFaq(item.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <Badge>{item.category}</Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Gamification Tab */}
          <TabsContent value="gamification" className="space-y-6">
            {/* Challenges Section */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Défis Hebdomadaires
                    </CardTitle>
                    <CardDescription>Gérer les défis et missions</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={async () => {
                      if (window.confirm('Réinitialiser tous les défis hebdomadaires ? Les progressions seront remises à zéro.')) {
                        try {
                          const res = await api.post('/challenges/reset-weekly');
                          toast.success(res.data.message);
                          fetchData();
                        } catch (error) {
                          toast.error('Erreur');
                        }
                      }
                    }}>
                      🔄 Reset hebdo
                    </Button>
                    <Dialog open={createChallengeOpen} onOpenChange={setCreateChallengeOpen}>
                      <DialogTrigger asChild>
                        <Button><Plus className="w-4 h-4 mr-2" />Nouveau défi</Button>
                      </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Créer un défi</DialogTitle>
                        <DialogDescription>Définir un nouveau défi pour la communauté</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await api.post('/challenges', newChallenge);
                          toast.success('Défi créé !');
                          setCreateChallengeOpen(false);
                          setNewChallenge({ title: '', description: '', challenge_type: 'messages', target_count: 5, xp_reward: 50, period: 'weekly', is_active: true });
                          fetchData();
                        } catch (error) {
                          toast.error('Erreur');
                        }
                      }} className="space-y-4">
                        <div>
                          <Label>Titre</Label>
                          <Input value={newChallenge.title} onChange={e => setNewChallenge({...newChallenge, title: e.target.value})} placeholder="Ex: Marin Bavard" required />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input value={newChallenge.description} onChange={e => setNewChallenge({...newChallenge, description: e.target.value})} placeholder="Ex: Envoie 5 messages" required />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Type</Label>
                            <Select value={newChallenge.challenge_type} onValueChange={v => setNewChallenge({...newChallenge, challenge_type: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="messages">Messages</SelectItem>
                                <SelectItem value="victories">Victoires</SelectItem>
                                <SelectItem value="events">Événements</SelectItem>
                                <SelectItem value="modules">Modules</SelectItem>
                                <SelectItem value="connections">Connexions</SelectItem>
                                <SelectItem value="difficulties">Difficultés</SelectItem>
                                <SelectItem value="congratulations">Félicitations</SelectItem>
                                <SelectItem value="declaratif">Déclaratif (hors plateforme, validé par IA/coach)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                              Les types de la liste se comptent automatiquement. « Déclaratif » ajoute un bouton
                              « Valider ce défi » côté membre avec justificatif vérifié par l'IA.
                            </p>
                          </div>
                          <div>
                            <Label>Objectif</Label>
                            <Input type="number" value={newChallenge.target_count} onChange={e => setNewChallenge({...newChallenge, target_count: parseInt(e.target.value)})} min={1} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>XP Reward</Label>
                            <Input type="number" value={newChallenge.xp_reward} onChange={e => setNewChallenge({...newChallenge, xp_reward: parseInt(e.target.value)})} min={1} />
                          </div>
                          <div>
                            <Label>Période</Label>
                            <Select value={newChallenge.period} onValueChange={v => setNewChallenge({...newChallenge, period: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                                <SelectItem value="monthly">Mensuel</SelectItem>
                                <SelectItem value="permanent">Permanent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="submit">Créer</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Défi</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Objectif</TableHead>
                      <TableHead>XP</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Actif</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {challenges.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{c.title}</p>
                            <p className="text-xs text-muted-foreground">{c.description}</p>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{c.challenge_type}</Badge></TableCell>
                        <TableCell>{c.target_count}</TableCell>
                        <TableCell className="text-primary font-bold">+{c.xp_reward}</TableCell>
                        <TableCell>
                          <Badge variant={c.period === 'permanent' ? 'secondary' : c.is_rotating ? 'default' : 'outline'}>
                            {c.period === 'permanent' ? '♾️ Permanent' : c.is_rotating ? '🔄 Rotatif' : '📅 Saisonnier'}
                          </Badge>
                        </TableCell>
                        <TableCell>{c.is_active ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}</TableCell>
                        <TableCell>
                          {!c.is_rotating && (
                            <Button variant="ghost" size="icon" onClick={async () => {
                              await api.delete(`/challenges/${c.id}`);
                              toast.success('Défi supprimé');
                              fetchData();
                            }}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-xs text-muted-foreground mt-4">
                  🔄 Défis rotatifs : 2 nouveaux défis tirés au sort chaque lundi à minuit<br/>
                  ♾️ Permanents : Toujours actifs, ne changent jamais<br/>
                  📅 Saisonniers : Créés et gérés manuellement par l&apos;admin
                </p>
              </CardContent>
            </Card>

            {/* Level Rewards Section */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5" />
                      Récompenses par Niveau
                    </CardTitle>
                    <CardDescription>Gérer les récompenses des 15 niveaux de progression</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {levelRewards.length > 0 ? (
                  <div className="space-y-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Niv</TableHead>
                          <TableHead>Titre niveau</TableHead>
                          <TableHead>Récompense</TableHead>
                          <TableHead className="w-20">Type</TableHead>
                          <TableHead className="w-20 text-right">Valeur</TableHead>
                          <TableHead className="w-20 text-right">XP</TableHead>
                          <TableHead className="w-24 text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {levelRewards.map((reward) => (
                          <TableRow key={reward.level} className={!reward.title ? 'opacity-50' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className="font-bold text-primary">{reward.level}</span>
                                {reward.frame && (
                                  <span className="text-xs" title={`Cadre ${reward.frame}`}>
                                    {reward.frame === 'bronze' ? '🟤' : reward.frame === 'silver' ? '⚪' : reward.frame === 'gold' ? '🟡' : '💎'}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{reward.level_title}</TableCell>
                            <TableCell>
                              {reward.title ? (
                                <div>
                                  <span className="mr-1">{reward.icon}</span>
                                  <span className="text-sm font-medium">{reward.title}</span>
                                  {!reward.is_default && <Badge variant="outline" className="ml-2 text-xs">Modifié</Badge>}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm italic">Pas de récompense</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {reward.reward_type && (
                                <Badge variant="secondary" className="text-xs">{reward.reward_type}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {reward.value > 0 && <span className="text-green-500 font-medium text-sm">{reward.value}€</span>}
                            </TableCell>
                            <TableCell className="text-right text-sm font-mono">{reward.xp_required.toLocaleString()}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => {
                                  setEditingReward({
                                    level: reward.level,
                                    title: reward.title || '',
                                    description: reward.description || '',
                                    reward_type: reward.reward_type || 'custom',
                                    value: reward.value || 0,
                                    icon: reward.icon || '🎁',
                                    requires_address: reward.requires_address || false,
                                    requires_recipient: reward.requires_recipient || false,
                                    requires_booking: reward.requires_booking || false,
                                  });
                                  setEditRewardOpen(true);
                                }} data-testid={`edit-reward-${reward.level}`}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                {!reward.is_default && (
                                  <Button variant="ghost" size="sm" onClick={async () => {
                                    try {
                                      await api.delete(`/level-rewards/${reward.level}`);
                                      toast.success('Réinitialisé aux valeurs par défaut');
                                      fetchData();
                                    } catch (e) { toast.error('Erreur'); }
                                  }} data-testid={`reset-reward-${reward.level}`}>
                                    <X className="w-3 h-3 text-orange-500" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Chargement des récompenses...</p>
                )}
              </CardContent>
            </Card>

            {/* Edit Reward Dialog */}
            <Dialog open={editRewardOpen} onOpenChange={setEditRewardOpen}>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle>Modifier la récompense - Niveau {editingReward?.level}</DialogTitle>
                  <DialogDescription>Modifie les détails de cette récompense</DialogDescription>
                </DialogHeader>
                {editingReward && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await api.post('/level-rewards', editingReward);
                      toast.success(`Récompense niveau ${editingReward.level} sauvegardée !`);
                      setEditRewardOpen(false);
                      setEditingReward(null);
                      fetchData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur');
                    }
                  }} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Icône (emoji)</Label>
                        <Input value={editingReward.icon} onChange={e => setEditingReward({...editingReward, icon: e.target.value})} placeholder="🎁" />
                      </div>
                      <div>
                        <Label>Type</Label>
                        <Select value={editingReward.reward_type} onValueChange={v => setEditingReward({...editingReward, reward_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="badge">Badge</SelectItem>
                            <SelectItem value="title">Titre</SelectItem>
                            <SelectItem value="badge_and_frame">Badge + Cadre</SelectItem>
                            <SelectItem value="physical">Physique</SelectItem>
                            <SelectItem value="coaching">Coaching</SelectItem>
                            <SelectItem value="gift">Cadeau à offrir</SelectItem>
                            <SelectItem value="physical_and_badge">Physique + Badge</SelectItem>
                            <SelectItem value="physical_and_frame">Physique + Cadre</SelectItem>
                            <SelectItem value="coaching_and_title">Coaching + Titre</SelectItem>
                            <SelectItem value="ultimate">Ultime</SelectItem>
                            <SelectItem value="custom">Personnalisé</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Titre de la récompense</Label>
                      <Input value={editingReward.title} onChange={e => setEditingReward({...editingReward, title: e.target.value})} placeholder="Ex: Box Surprise Odys" required />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={editingReward.description} onChange={e => setEditingReward({...editingReward, description: e.target.value})} placeholder="Description visible par les membres" required rows={2} />
                    </div>
                    <div>
                      <Label>Valeur estimée (€)</Label>
                      <Input type="number" value={editingReward.value} onChange={e => setEditingReward({...editingReward, value: parseInt(e.target.value) || 0})} min={0} />
                    </div>
                    <div className="space-y-3 border rounded-lg p-3">
                      <p className="text-sm font-medium text-muted-foreground">Options de réclamation</p>
                      <div className="flex items-center gap-2">
                        <Switch checked={editingReward.requires_address} onCheckedChange={v => setEditingReward({...editingReward, requires_address: v})} />
                        <Label className="text-sm">Nécessite une adresse postale</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={editingReward.requires_recipient} onCheckedChange={v => setEditingReward({...editingReward, requires_recipient: v})} />
                        <Label className="text-sm">Nécessite un destinataire (cadeau)</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={editingReward.requires_booking} onCheckedChange={v => setEditingReward({...editingReward, requires_booking: v})} />
                        <Label className="text-sm">Nécessite une réservation (coaching)</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" type="button" onClick={() => setEditRewardOpen(false)}>Annuler</Button>
                      <Button type="submit"><Save className="w-4 h-4 mr-2" />Sauvegarder</Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Ambassador Tab */}
          <TabsContent value="ambassador" className="space-y-6">
            {/* Tiers Configuration */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="w-5 h-5" />
                      Paliers de Commission
                    </CardTitle>
                    <CardDescription>Configurer les pourcentages par nombre de filleuls</CardDescription>
                  </div>
                  <Dialog open={editTiersOpen} onOpenChange={(open) => {
                    setEditTiersOpen(open);
                    if (open) setEditedTiers([...ambassadorTiers]);
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline"><Edit className="w-4 h-4 mr-2" />Modifier</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Modifier les paliers</DialogTitle>
                        <DialogDescription>Ajuster les commissions par palier</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        {editedTiers.map((tier, idx) => (
                          <div key={tier.id || `tier-${idx}`} className="grid grid-cols-3 gap-2 items-center">
                            <Input value={tier.tier_name} onChange={e => {
                              const newTiers = [...editedTiers];
                              newTiers[idx].tier_name = e.target.value;
                              setEditedTiers(newTiers);
                            }} placeholder="Nom" />
                            <Input type="number" value={tier.min_referrals} onChange={e => {
                              const newTiers = [...editedTiers];
                              newTiers[idx].min_referrals = parseInt(e.target.value);
                              setEditedTiers(newTiers);
                            }} placeholder="Min filleuls" />
                            <div className="flex items-center gap-1">
                              <Input type="number" value={tier.commission_percent} onChange={e => {
                                const newTiers = [...editedTiers];
                                newTiers[idx].commission_percent = parseFloat(e.target.value);
                                setEditedTiers(newTiers);
                              }} placeholder="%" />
                              <span>%</span>
                            </div>
                          </div>
                        ))}
                        <Button variant="outline" onClick={() => setEditedTiers([...editedTiers, { tier_name: '', min_referrals: 0, commission_percent: 0 }])}>
                          <Plus className="w-4 h-4 mr-2" />Ajouter palier
                        </Button>
                      </div>
                      <DialogFooter>
                        <Button onClick={async () => {
                          try {
                            await api.post('/ambassador/tiers', editedTiers);
                            toast.success('Paliers mis à jour !');
                            setEditTiersOpen(false);
                            fetchData();
                          } catch (error) {
                            toast.error('Erreur');
                          }
                        }}>Enregistrer</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {ambassadorTiers.map(tier => (
                    <Card key={tier.id} className="text-center">
                      <CardContent className="p-4">
                        <p className="text-3xl font-bold text-primary">{tier.commission_percent}%</p>
                        <p className="font-medium">{tier.tier_name}</p>
                        <p className="text-sm text-muted-foreground">{tier.min_referrals}+ filleuls</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pending Referrals */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Parrainages en attente
                </CardTitle>
                <CardDescription>Valider les parrainages après achat</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingReferrals.filter(r => r.status === 'pending').length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Parrain</TableHead>
                        <TableHead>Filleul</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReferralsList.map(ref => (
                        <TableRow key={ref.id}>
                          <TableCell>{ref.referrer_name || ref.referrer_email}</TableCell>
                          <TableCell>{ref.referee_email}</TableCell>
                          <TableCell>{new Date(ref.created_at).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>{ref.purchase_amount ? `${ref.purchase_amount}€` : '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm"><Check className="w-4 h-4 mr-1" />Valider</Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Valider le parrainage</DialogTitle>
                                    <DialogDescription>Entrez le montant d&apos;achat pour calculer la commission</DialogDescription>
                                  </DialogHeader>
                                  <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    const amount = parseFloat(e.target.amount.value);
                                    try {
                                      const res = await api.post(`/ambassador/referrals/${ref.id}/validate?purchase_amount=${amount}`);
                                      toast.success(`Commission: ${res.data.commission_amount}€`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Erreur');
                                    }
                                  }}>
                                    <div className="space-y-4">
                                      <div>
                                        <Label>Montant d&apos;achat (€)</Label>
                                        <Input name="amount" type="number" step="0.01" defaultValue={ref.purchase_amount || ''} required />
                                      </div>
                                      <DialogFooter>
                                        <Button type="submit">Valider et calculer commission</Button>
                                      </DialogFooter>
                                    </div>
                                  </form>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Aucun parrainage en attente de validation</p>
                )}
              </CardContent>
            </Card>

            {/* All Referrals History */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle>Historique des parrainages</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parrain</TableHead>
                      <TableHead>Filleul</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingReferrals.map(ref => (
                      <TableRow key={ref.id}>
                        <TableCell>{ref.referrer_name || ref.referrer_email}</TableCell>
                        <TableCell>{ref.referee_email}</TableCell>
                        <TableCell>
                          <Badge variant={ref.status === 'validated' ? 'default' : ref.status === 'pending' ? 'secondary' : 'destructive'}>
                            {ref.status === 'validated' ? 'Validé' : ref.status === 'pending' ? 'En attente' : 'Rejeté'}
                          </Badge>
                        </TableCell>
                        <TableCell>{ref.purchase_amount ? `${ref.purchase_amount}€` : '-'}</TableCell>
                        <TableCell className="text-green-500 font-bold">{ref.commission_amount ? `+${ref.commission_amount}€` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            {/* Payout Requests */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Demandes de retrait ({pendingPayouts.filter(p => p.status === 'pending').length} en attente)
                </CardTitle>
                <CardDescription>Valider les demandes de versement des commissions</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingPayouts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ambassadeur</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead>IBAN</TableHead>
                        <TableHead>Titulaire</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingPayouts.map(payout => (
                        <TableRow key={payout.id} className={payout.status === 'pending' ? 'bg-orange-500/5' : ''}>
                          <TableCell className="font-medium">{payout.ambassador_name}</TableCell>
                          <TableCell className="text-right font-bold text-green-500">{payout.amount?.toFixed(2)} EUR</TableCell>
                          <TableCell className="font-mono text-xs">{payout.iban ? `...${payout.iban.slice(-8)}` : '-'}</TableCell>
                          <TableCell>{payout.holder_name || '-'}</TableCell>
                          <TableCell className="text-sm">{payout.created_at ? new Date(payout.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                          <TableCell>
                            <Badge variant={payout.status === 'paid' ? 'default' : payout.status === 'rejected' ? 'destructive' : 'outline'}
                              className={payout.status === 'paid' ? 'bg-green-500' : ''}>
                              {payout.status === 'pending' ? 'En attente' : payout.status === 'paid' ? 'Paye' : 'Refuse'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {payout.status === 'pending' && (
                              <div className="flex gap-1">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={async () => {
                                  try {
                                    await api.post(`/admin/ambassador/payouts/${payout.id}/pay`);
                                    toast.success('Marque comme paye !');
                                    fetchData();
                                  } catch (e) { toast.error('Erreur'); }
                                }} data-testid={`pay-payout-${payout.id}`}>
                                  <Check className="w-3 h-3 mr-1" />Payer
                                </Button>
                                <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={async () => {
                                  try {
                                    await api.post(`/admin/ambassador/payouts/${payout.id}/reject`);
                                    toast.success('Demande refusee');
                                    fetchData();
                                  } catch (e) { toast.error('Erreur'); }
                                }}>
                                  <X className="w-3 h-3 mr-1" />Refuser
                                </Button>
                              </div>
                            )}
                            {payout.status === 'paid' && payout.paid_at && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(payout.paid_at).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Aucune demande de retrait</p>
                )}
              </CardContent>
            </Card>

          </TabsContent>

          {/* PARCOURS TAB - Chapters & Milestones */}
          <TabsContent value="parcours" className="space-y-6">
            {/* Chapters Management */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Compass className="w-5 h-5 text-primary" />
                      Chapitres du parcours
                    </CardTitle>
                    <CardDescription>Les étapes de la formation</CardDescription>
                  </div>
                  <Dialog open={createChapterOpen} onOpenChange={setCreateChapterOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary" data-testid="add-chapter-btn">
                        <Plus className="w-4 h-4 mr-2" />Ajouter un chapitre
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border">
                      <DialogHeader>
                        <DialogTitle>Nouveau chapitre</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await api.post('/admin/chapters', newChapter);
                          toast.success('Chapitre créé !');
                          setCreateChapterOpen(false);
                          setNewChapter({ title: '', description: '', icon: '📚', xp_reward: 50 });
                          fetchData();
                        } catch (error) {
                          toast.error('Erreur');
                        }
                      }} className="space-y-4">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-1">
                            <Label>Icône</Label>
                            <Input value={newChapter.icon} onChange={(e) => setNewChapter({...newChapter, icon: e.target.value})} className="bg-secondary/50 text-2xl text-center" maxLength={2} />
                          </div>
                          <div className="col-span-3">
                            <Label>Titre *</Label>
                            <Input value={newChapter.title} onChange={(e) => setNewChapter({...newChapter, title: e.target.value})} className="bg-secondary/50" required />
                          </div>
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea value={newChapter.description} onChange={(e) => setNewChapter({...newChapter, description: e.target.value})} className="bg-secondary/50" rows={2} />
                        </div>
                        <div>
                          <Label>XP Récompense</Label>
                          <Input type="number" value={newChapter.xp_reward} onChange={(e) => setNewChapter({...newChapter, xp_reward: parseInt(e.target.value) || 50})} className="bg-secondary/50" />
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="bg-primary">Créer</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="outline"
                    onClick={generateMissingKeyPoints}
                    disabled={generatingKeyPoints}
                    data-testid="generate-missing-key-points"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generatingKeyPoints ? 'Génération...' : 'Points clés manquants (IA)'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-32">Étape</TableHead>
                      <TableHead>Chapitre</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24">XP</TableHead>
                      <TableHead className="w-44">Question de contrôle</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chapters.map((ch) => (
                      <TableRow key={ch.id}>
                        <TableCell>
                          <span className="text-xl">{ch.icon}</span>
                          <span className="ml-1 text-xs text-muted-foreground">{ch.label || ch.id}</span>
                        </TableCell>
                        <TableCell className="font-medium">{ch.title}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{ch.description}</TableCell>
                        <TableCell><Badge variant="secondary">+{ch.xp_reward} XP</Badge></TableCell>
                        <TableCell data-testid={`chapter-quiz-state-${ch.id}`}>
                          {(ch.key_points || '').trim().length < 40 ? (
                            <span className="text-xs text-muted-foreground">Aucun point clé</span>
                          ) : ch.key_points_source === 'ai_draft' && !ch.key_points_validated ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">Brouillon IA</Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={() => validateKeyPoints(ch.id)}
                                data-testid={`validate-key-points-${ch.id}`}
                              >
                                Relu, activer
                              </Button>
                            </div>
                          ) : (
                            <Badge className="text-xs bg-green-500/15 text-green-700">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" data-testid={`edit-chapter-${ch.id}`} onClick={() => { setEditingChapter(ch); setEditChapterOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-chapter-${ch.id}`} onClick={async () => {
                              if (confirm('Supprimer ce chapitre ?')) {
                                await api.delete(`/admin/chapters/${ch.id}`);
                                toast.success('Chapitre supprimé');
                                fetchData();
                              }
                            }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Milestones Management */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      Jalons personnels
                    </CardTitle>
                    <CardDescription>Les accomplissements à débloquer</CardDescription>
                  </div>
                  <Dialog open={createMilestoneOpen} onOpenChange={setCreateMilestoneOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-amber-500 hover:bg-amber-600" data-testid="add-milestone-btn">
                        <Plus className="w-4 h-4 mr-2" />Ajouter un jalon
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border">
                      <DialogHeader>
                        <DialogTitle>Nouveau jalon</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await api.post('/admin/milestones', {
                            ...newMilestone,
                            id: newMilestone.id || newMilestone.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                          });
                          toast.success('Jalon créé !');
                          setCreateMilestoneOpen(false);
                          setNewMilestone({ id: '', title: '', description: '', icon: '🎯', xp_reward: 100 });
                          fetchData();
                        } catch (error) {
                          toast.error('Erreur');
                        }
                      }} className="space-y-4">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-1">
                            <Label>Icône</Label>
                            <Input value={newMilestone.icon} onChange={(e) => setNewMilestone({...newMilestone, icon: e.target.value})} className="bg-secondary/50 text-2xl text-center" maxLength={2} />
                          </div>
                          <div className="col-span-3">
                            <Label>Titre *</Label>
                            <Input value={newMilestone.title} onChange={(e) => setNewMilestone({...newMilestone, title: e.target.value})} className="bg-secondary/50" required />
                          </div>
                        </div>
                        <div>
                          <Label>ID (optionnel, généré automatiquement)</Label>
                          <Input value={newMilestone.id} onChange={(e) => setNewMilestone({...newMilestone, id: e.target.value})} className="bg-secondary/50" placeholder="ex: first_property" />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea value={newMilestone.description} onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})} className="bg-secondary/50" rows={2} />
                        </div>
                        <div>
                          <Label>XP Récompense</Label>
                          <Input type="number" value={newMilestone.xp_reward} onChange={(e) => setNewMilestone({...newMilestone, xp_reward: parseInt(e.target.value) || 100})} className="bg-secondary/50" />
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="bg-amber-500 hover:bg-amber-600">Créer</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Icône</TableHead>
                      <TableHead>Jalon</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24">XP</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {milestones.map((ms) => (
                      <TableRow key={ms.id}>
                        <TableCell><span className="text-xl">{ms.icon}</span></TableCell>
                        <TableCell className="font-medium">{ms.title}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{ms.description}</TableCell>
                        <TableCell><Badge className="bg-amber-500/20 text-amber-500">+{ms.xp_reward} XP</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" data-testid={`edit-milestone-${ms.id}`} onClick={() => { setEditingMilestone(ms); setEditMilestoneOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-milestone-${ms.id}`} onClick={async () => {
                              if (confirm('Supprimer ce jalon ?')) {
                                await api.delete(`/admin/milestones/${ms.id}`);
                                toast.success('Jalon supprimé');
                                fetchData();
                              }
                            }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Chapter Dialog */}
      <Dialog open={editChapterOpen} onOpenChange={setEditChapterOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Modifier le chapitre</DialogTitle>
          </DialogHeader>
          {editingChapter && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.put(`/admin/chapters/${editingChapter.id}`, editingChapter);
                toast.success('Chapitre mis à jour');
                setEditChapterOpen(false);
                fetchData();
              } catch (error) {
                toast.error('Erreur');
              }
            }} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <Label>Icône</Label>
                  <Input value={editingChapter.icon} onChange={(e) => setEditingChapter({...editingChapter, icon: e.target.value})} className="bg-secondary/50 text-2xl text-center" maxLength={2} />
                </div>
                <div className="col-span-3">
                  <Label>Titre</Label>
                  <Input value={editingChapter.title} onChange={(e) => setEditingChapter({...editingChapter, title: e.target.value})} className="bg-secondary/50" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editingChapter.description} onChange={(e) => setEditingChapter({...editingChapter, description: e.target.value})} className="bg-secondary/50" rows={2} />
              </div>
              <div>
                <Label>XP Récompense</Label>
                <Input type="number" value={editingChapter.xp_reward} onChange={(e) => setEditingChapter({...editingChapter, xp_reward: parseInt(e.target.value) || 50})} className="bg-secondary/50" />
              </div>
              <div>
                <Label>Lien de la leçon (ThriveCart)</Label>
                <Input
                  value={editingChapter.lesson_url || ''}
                  onChange={(e) => setEditingChapter({...editingChapter, lesson_url: e.target.value})}
                  className="bg-secondary/50"
                  placeholder="https://learn.thrivecart.com/.../lesson/..."
                  data-testid="chapter-lesson-url"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Colle l'URL de la leçon correspondante : un bouton « Ouvrir la leçon » apparaîtra sur ce chapitre
                  dans l'Odyssée, et au retour le membre pourra valider en un clic.
                </p>
              </div>
              <div>
                <Label>Points clés enseignés (question de contrôle IA)</Label>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs text-muted-foreground">
                    Vide = pas de question de contrôle. Rempli et relu = impossible de valider le chapitre sans l'avoir suivi.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => generateKeyPointsFor(editingChapter.id)}
                    disabled={generatingKeyPoints}
                    data-testid="generate-key-points-single"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {generatingKeyPoints ? 'Génération...' : 'Proposer avec l\'IA'}
                  </Button>
                </div>
                <Textarea
                  value={editingChapter.key_points || ''}
                  onChange={(e) => setEditingChapter({...editingChapter, key_points: e.target.value})}
                  className="bg-secondary/50"
                  rows={8}
                  placeholder="Colle ici les notions enseignées dans ce chapitre (chiffres, règles, méthodes). Dès 40 caractères, l'IA génère une question de contrôle que le membre doit réussir pour valider le chapitre."
                  data-testid="chapter-key-points"
                />
                {editingChapter.key_points_source === 'ai_draft' && !editingChapter.key_points_validated && (
                  <p className="text-xs text-amber-600 mt-1">
                    Brouillon rédigé par IA : relis et corrige selon ton contenu réel, puis enregistre pour activer la question.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditChapterOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-primary">Sauvegarder</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={editMilestoneOpen} onOpenChange={setEditMilestoneOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Modifier le jalon</DialogTitle>
          </DialogHeader>
          {editingMilestone && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.put(`/admin/milestones/${editingMilestone.id}`, editingMilestone);
                toast.success('Jalon mis à jour');
                setEditMilestoneOpen(false);
                fetchData();
              } catch (error) {
                toast.error('Erreur');
              }
            }} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <Label>Icône</Label>
                  <Input value={editingMilestone.icon} onChange={(e) => setEditingMilestone({...editingMilestone, icon: e.target.value})} className="bg-secondary/50 text-2xl text-center" maxLength={2} />
                </div>
                <div className="col-span-3">
                  <Label>Titre</Label>
                  <Input value={editingMilestone.title} onChange={(e) => setEditingMilestone({...editingMilestone, title: e.target.value})} className="bg-secondary/50" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editingMilestone.description} onChange={(e) => setEditingMilestone({...editingMilestone, description: e.target.value})} className="bg-secondary/50" rows={2} />
              </div>
              <div>
                <Label>XP Récompense</Label>
                <Input type="number" value={editingMilestone.xp_reward} onChange={(e) => setEditingMilestone({...editingMilestone, xp_reward: parseInt(e.target.value) || 100})} className="bg-secondary/50" />
              </div>
              <div>
                <Label>Mode de vérification</Label>
                <Select
                  value={editingMilestone.verification || 'coach'}
                  onValueChange={(v) => setEditingMilestone({...editingMilestone, verification: v})}
                >
                  <SelectTrigger className="bg-secondary/50 mt-1" data-testid="milestone-verification-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatique (suivi par la plateforme)</SelectItem>
                    <SelectItem value="ai">Vérification IA du justificatif</SelectItem>
                    <SelectItem value="coach">Validation coach obligatoire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditMilestoneOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600">Sauvegarder</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={editMemberOpen} onOpenChange={setEditMemberOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le membre</DialogTitle>
            <DialogDescription>{editingMember?.first_name} {editingMember?.last_name}</DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={editingMember.role} onValueChange={(v) => setEditingMember({ ...editingMember, role: v })}>
                  <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <Label>Compte actif</Label>
                <Switch checked={editingMember.is_active} onCheckedChange={(c) => setEditingMember({ ...editingMember, is_active: c })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMemberOpen(false)}>Annuler</Button>
                <Button onClick={handleUpdateMember} className="bg-primary text-primary-foreground"><Save className="w-4 h-4 mr-2" />Sauvegarder</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Channel Dialog */}
      <Dialog open={editChannelOpen} onOpenChange={setEditChannelOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le channel</DialogTitle>
            <DialogDescription>#{editingChannel?.name}</DialogDescription>
          </DialogHeader>
          {editingChannel && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={editingChannel.name} onChange={(e) => setEditingChannel({ ...editingChannel, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="bg-secondary/50" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editingChannel.description || ''} onChange={(e) => setEditingChannel({ ...editingChannel, description: e.target.value })} className="bg-secondary/50" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Type de channel</Label>
                <Select value={editingChannel.channel_type} onValueChange={(v) => setEditingChannel({ ...editingChannel, channel_type: v })}>
                  <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {channelTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <div className="flex items-center gap-2">{t.icon}{t.label}</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Groupe (glisser dans un groupe)</Label>
                <Select value={editingChannel.group_id || 'none'} onValueChange={(v) => setEditingChannel({ ...editingChannel, group_id: v })}>
                  <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun groupe</SelectItem>
                    {channelGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-2">
                  {editingChannel.is_private ? <Lock className="w-4 h-4 text-primary" /> : <Unlock className="w-4 h-4" />}
                  <Label>Channel privé</Label>
                </div>
                <Switch checked={editingChannel.is_private} onCheckedChange={(c) => setEditingChannel({ ...editingChannel, is_private: c })} />
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-yellow-500" />
                  <div>
                    <Label>Lecture seule</Label>
                    <p className="text-xs text-muted-foreground">Seuls Admin et Major peuvent poster</p>
                  </div>
                </div>
                <Switch checked={editingChannel.is_read_only || false} onCheckedChange={(c) => setEditingChannel({ ...editingChannel, is_read_only: c })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditChannelOpen(false)}>Annuler</Button>
                <Button onClick={handleUpdateChannel} className="bg-primary text-primary-foreground"><Save className="w-4 h-4 mr-2" />Sauvegarder</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
